import React from 'react';
import { FileUpload } from './FileUpload';
import { inputClass } from '../ui/Field';
import { COVER_LETTER_LIMITS } from '../../data/careers';
export type CoverLetterMode = 'upload' | 'write';
interface CoverLetterToggleProps {
  mode: CoverLetterMode | '';
  onModeChange: (mode: CoverLetterMode) => void;
  file: File | null;
  onFileChange: (file: File | null) => void;
  text: string;
  onTextChange: (text: string) => void;
  progress?: number | null;
  errors: {
    mode?: string;
    file?: string;
    text?: string;
  };
  disabled?: boolean;
}
const OPTIONS: {
  value: CoverLetterMode;
  label: string;
}[] = [{
  value: 'upload',
  label: 'Upload a cover letter'
}, {
  value: 'write',
  label: 'Write your cover letter here'
}];

/** One mode at a time. Switching is handled by the parent, which clears the other input. */
export function CoverLetterToggle({
  mode,
  onModeChange,
  file,
  onFileChange,
  text,
  onTextChange,
  progress,
  errors,
  disabled
}: CoverLetterToggleProps) {
  const length = text.trim().length;
  const counterTone = length === 0 ? 'text-muted' : length < COVER_LETTER_LIMITS.min || length > COVER_LETTER_LIMITS.max ? 'text-[#FF8B7A]' : 'text-[#6FD39A]';
  return <fieldset aria-describedby={errors.mode ? 'cover-mode-error' : undefined} data-id="element-652" data-mp="4lojd48wglsi">
      <legend className="text-[14px] font-medium text-ink" data-id="element-653" data-mp="3vshk66zs6iwi">Cover letter</legend>
      <div role="radiogroup" id="cover-mode" className="mt-3 grid gap-3 sm:grid-cols-2" tabIndex={-1} data-id="element-654" data-mp="383gir3pd880q">
        {OPTIONS.map(option => {
        const checked = mode === option.value;
        return <label key={option.value} className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-sm border px-4 py-3 text-[15px] transition-colors duration-150 ${checked ? 'border-gold bg-panel text-ink' : 'border-line bg-charcoal text-body hover:border-gold/60'}`} data-id="element-655" data-mp="3i1cmvbjvy2pc">
              <input type="radio" name="cover-letter-mode" value={option.value} checked={checked} disabled={disabled} onChange={() => onModeChange(option.value)} className="h-4 w-4 shrink-0 accent-[#FF5C1A]" data-id="element-656" data-mp="sc0zh2eatou6" />
              {option.label}
            </label>;
      })}
      </div>
      {errors.mode && <p id="cover-mode-error" role="alert" className="mt-2 text-small text-[#FF8B7A]" data-id="element-657" data-mp="gp9c5hp4uo22">
          {errors.mode}
        </p>}

      {mode === 'upload' && <div className="mt-5" data-id="element-658" data-mp="llzqo0lahbld">
          <FileUpload id="cover-file" label="Your cover letter" required file={file} onChange={onFileChange} error={errors.file} progress={progress} disabled={disabled} data-id="element-659" data-mp="qngnnl0nkdwo" />
        </div>}

      {mode === 'write' && <div className="mt-5" data-id="element-660" data-mp="2wkqs9hcasik">
          <label htmlFor="cover-text" className="block text-[14px] font-medium text-ink" data-id="element-661" data-mp="2vrobkpeyp68">
            Your cover letter
          </label>
          <p id="cover-text-hint" className="mt-1 text-small text-muted" data-id="element-662" data-mp="2c6k5a8gnor5u">
            Between {COVER_LETTER_LIMITS.min} and {COVER_LETTER_LIMITS.max.toLocaleString('en-GB')} characters.
            Tell us why this role, and what you would want to own in your first month.
          </p>
          <textarea id="cover-text" value={text} disabled={disabled} onChange={event => onTextChange(event.target.value)} maxLength={COVER_LETTER_LIMITS.max + 200} aria-invalid={errors.text ? true : undefined} aria-describedby={['cover-text-hint', 'cover-text-count', errors.text ? 'cover-text-error' : ''].filter(Boolean).join(' ')} className={`${inputClass} mt-2 min-h-[200px] resize-y sm:min-h-[300px]`} data-id="element-663" data-mp="1x9047cmvrg4y" />
          <p id="cover-text-count" className={`mt-1.5 text-right text-small ${counterTone}`} aria-live="polite" data-id="element-664" data-mp="19kqmomwp9ejm">
            {length.toLocaleString('en-GB')} / {COVER_LETTER_LIMITS.max.toLocaleString('en-GB')}
            {length > 0 && length < COVER_LETTER_LIMITS.min && `, ${COVER_LETTER_LIMITS.min - length} more needed`}
          </p>
          {errors.text && <p id="cover-text-error" role="alert" className="mt-1 text-small text-[#FF8B7A]" data-id="element-665" data-mp="34ugibbd4ihwp">
              {errors.text}
            </p>}
        </div>}
    </fieldset>;
}